/**
 * Contact Fields Service
 * Provides CRM contact/company/deal/sender fields for template variable insertion
 *
 * Recent: 2026-05-11 — **`company_revenue`** / **`deal_amount`** example strings use **£** (GBP-aligned demo copy).
 */

export interface ContactField {
  id: string;
  name: string;
  label: string;
  type: string;
  category: 'contact' | 'company' | 'deal' | 'custom';
  placeholder: string;
  example: string;
}

export interface FieldCategory {
  id: string;
  label: string;
  icon: string;
  fields: ContactField[];
}

const STANDARD_CONTACT_FIELDS: ContactField[] = [
  { id: 'first_name', name: 'first_name', label: 'First Name', type: 'text', category: 'contact', placeholder: '{FIRST_NAME}', example: 'John' },
  { id: 'last_name', name: 'last_name', label: 'Last Name', type: 'text', category: 'contact', placeholder: '{LAST_NAME}', example: 'Doe' },
  { id: 'full_name', name: 'full_name', label: 'Full Name', type: 'text', category: 'contact', placeholder: '{FULL_NAME}', example: 'John Doe' },
  { id: 'email', name: 'email', label: 'Email', type: 'email', category: 'contact', placeholder: '{EMAIL}', example: 'john.doe@example.com' },
  { id: 'phone', name: 'phone', label: 'Phone', type: 'phone', category: 'contact', placeholder: '{PHONE}', example: '+1 (555) 123-4567' },
  { id: 'mobile', name: 'mobile', label: 'Mobile', type: 'phone', category: 'contact', placeholder: '{MOBILE}', example: '+1 (555) 987-6543' },
  { id: 'title', name: 'title', label: 'Job Title', type: 'text', category: 'contact', placeholder: '{TITLE}', example: 'VP of Sales' },
  { id: 'department', name: 'department', label: 'Department', type: 'text', category: 'contact', placeholder: '{DEPARTMENT}', example: 'Sales' },
  { id: 'address', name: 'address', label: 'Address', type: 'text', category: 'contact', placeholder: '{ADDRESS}', example: '123 Main St' },
  { id: 'city', name: 'city', label: 'City', type: 'text', category: 'contact', placeholder: '{CITY}', example: 'San Francisco' },
  { id: 'state', name: 'state', label: 'State/Province', type: 'text', category: 'contact', placeholder: '{STATE}', example: 'CA' },
  { id: 'zip', name: 'zip', label: 'Zip/Postal Code', type: 'text', category: 'contact', placeholder: '{ZIP}', example: '94102' },
  { id: 'country', name: 'country', label: 'Country', type: 'text', category: 'contact', placeholder: '{COUNTRY}', example: 'United States' },
  { id: 'linkedin', name: 'linkedin', label: 'LinkedIn', type: 'url', category: 'contact', placeholder: '{LINKEDIN}', example: 'linkedin.com/in/johndoe' },
  { id: 'twitter', name: 'twitter', label: 'Twitter', type: 'text', category: 'contact', placeholder: '{TWITTER}', example: '@johndoe' },
];

const COMPANY_FIELDS: ContactField[] = [
  { id: 'company_name', name: 'company.name', label: 'Company Name', type: 'text', category: 'company', placeholder: '{COMPANY_NAME}', example: 'Acme Corp' },
  { id: 'company_website', name: 'company.website', label: 'Company Website', type: 'url', category: 'company', placeholder: '{COMPANY_WEBSITE}', example: 'acmecorp.com' },
  { id: 'company_industry', name: 'company.industry', label: 'Industry', type: 'text', category: 'company', placeholder: '{COMPANY_INDUSTRY}', example: 'Technology' },
  { id: 'company_size', name: 'company.size', label: 'Company Size', type: 'text', category: 'company', placeholder: '{COMPANY_SIZE}', example: '100-500' },
  { id: 'company_revenue', name: 'company.revenue', label: 'Annual Revenue', type: 'text', category: 'company', placeholder: '{COMPANY_REVENUE}', example: '£10M–£50M' },
  { id: 'company_address', name: 'company.address', label: 'Company Address', type: 'text', category: 'company', placeholder: '{COMPANY_ADDRESS}', example: '456 Business Blvd' },
  { id: 'company_city', name: 'company.city', label: 'Company City', type: 'text', category: 'company', placeholder: '{COMPANY_CITY}', example: 'New York' },
  { id: 'company_state', name: 'company.state', label: 'Company State', type: 'text', category: 'company', placeholder: '{COMPANY_STATE}', example: 'NY' },
  { id: 'company_country', name: 'company.country', label: 'Company Country', type: 'text', category: 'company', placeholder: '{COMPANY_COUNTRY}', example: 'United States' },
];

const DEAL_FIELDS: ContactField[] = [
  { id: 'deal_name', name: 'deal.name', label: 'Deal Name', type: 'text', category: 'deal', placeholder: '{DEAL_NAME}', example: 'Q1 Enterprise Deal' },
  { id: 'deal_amount', name: 'deal.amount', label: 'Deal Amount', type: 'currency', category: 'deal', placeholder: '{DEAL_AMOUNT}', example: '£50,000' },
  { id: 'deal_stage', name: 'deal.stage', label: 'Deal Stage', type: 'text', category: 'deal', placeholder: '{DEAL_STAGE}', example: 'Proposal Sent' },
  { id: 'deal_close_date', name: 'deal.close_date', label: 'Expected Close Date', type: 'date', category: 'deal', placeholder: '{DEAL_CLOSE_DATE}', example: 'March 31, 2026' },
];

const SENDER_FIELDS: ContactField[] = [
  { id: 'sender_name', name: 'sender.name', label: 'Sender Name', type: 'text', category: 'custom', placeholder: '{SENDER_NAME}', example: 'Jane Smith' },
  { id: 'sender_email', name: 'sender.email', label: 'Sender Email', type: 'email', category: 'custom', placeholder: '{SENDER_EMAIL}', example: 'jane.smith@company.com' },
  { id: 'sender_title', name: 'sender.title', label: 'Sender Title', type: 'text', category: 'custom', placeholder: '{SENDER_TITLE}', example: 'Sales Representative' },
  { id: 'sender_phone', name: 'sender.phone', label: 'Sender Phone', type: 'phone', category: 'custom', placeholder: '{SENDER_PHONE}', example: '+1 (555) 999-8888' },
  { id: 'sender_signature', name: 'sender.signature', label: 'Sender Signature', type: 'text', category: 'custom', placeholder: '{SENDER_SIGNATURE}', example: 'Best regards,\nJane Smith' },
];

export class ContactFieldsService {
  static getAllFieldCategories(): FieldCategory[] {
    return [
      { id: 'contact', label: 'Contact Fields', icon: '👤', fields: STANDARD_CONTACT_FIELDS },
      { id: 'company', label: 'Company Fields', icon: '🏢', fields: COMPANY_FIELDS },
      { id: 'deal', label: 'Deal Fields', icon: '💼', fields: DEAL_FIELDS },
      { id: 'sender', label: 'Sender Fields', icon: '✉️', fields: SENDER_FIELDS },
    ];
  }

  static getAllFields(): ContactField[] {
    return [
      ...STANDARD_CONTACT_FIELDS,
      ...COMPANY_FIELDS,
      ...DEAL_FIELDS,
      ...SENDER_FIELDS,
    ];
  }

  static getFieldsByCategory(category: string): ContactField[] {
    const map: Record<string, ContactField[]> = {
      contact: STANDARD_CONTACT_FIELDS,
      company: COMPANY_FIELDS,
      deal: DEAL_FIELDS,
      sender: SENDER_FIELDS,
    };
    return map[category] ?? [];
  }

  static searchFields(query: string): ContactField[] {
    const lower = query.toLowerCase();
    return this.getAllFields().filter(
      (f) =>
        f.label.toLowerCase().includes(lower) ||
        f.name.toLowerCase().includes(lower) ||
        f.placeholder.toLowerCase().includes(lower)
    );
  }

  static getFieldByPlaceholder(placeholder: string): ContactField | undefined {
    return this.getAllFields().find((f) => f.placeholder === placeholder);
  }

  static getExampleValue(placeholder: string): string {
    const field = this.getFieldByPlaceholder(placeholder);
    return field?.example ?? placeholder;
  }

  /** Replace placeholders like {FIRST_NAME} or {{contact.first_name}} with example values */
  static replaceAllPlaceholders(text: string): string {
    let result = text;
    result = result.replace(/\{([A-Z0-9_]+)\}/g, (match) => this.getExampleValue(match));
    result = result.replace(/\{\{contact\.first_name\}\}/g, 'John');
    result = result.replace(/\{\{contact\.last_name\}\}/g, 'Doe');
    result = result.replace(/\{\{contact\.full_name\}\}/g, 'John Doe');
    result = result.replace(/\{\{contact\.email\}\}/g, 'john.doe@example.com');
    result = result.replace(/\{\{company\.name\}\}/g, 'Acme Corp');
    result = result.replace(/\{\{company\.industry\}\}/g, 'Technology');
    result = result.replace(/\{\{sender\.full_name\}\}/g, 'Jane Smith');
    result = result.replace(/\{\{sender\.title\}\}/g, 'Sales Representative');
    return result;
  }

  /** Extract unique placeholders from text */
  static extractPlaceholders(text: string): string[] {
    const matches = text.match(/\{([A-Z0-9_]+)\}/g);
    return [...new Set(matches ?? [])];
  }
}
